import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PeriodPanel,
	SectionCard,
	EmptyState,
} from '@practium/ui';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import { useCallback, useEffect, useState } from 'react';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { RecommendationsTable } from '../components/Tables/RecommendationsTable/RecommendationsTable';
import type { UseRecommendationsPageProps } from '../hooks/useRecommendationsPage';
import { useRecommendationsPage } from '../hooks/useRecommendationsPage';
import type { RecommendationsTab } from '../types';
import { RecommendationsPageSkeleton } from '../components/Skeletons/RecommendationsPageSkeleton';

export interface RecommendationsPageProps extends UseRecommendationsPageProps {
	onAddNew?: (tab: RecommendationsTab) => void;
	onViewItem?: (tab: RecommendationsTab, id: string) => void;
	onReportClick?: () => void;
	onTabChange?: (tab: RecommendationsTab) => void;
}

export const RecommendationsPage = ({
	onAddNew,
	onViewItem,
	onReportClick,
	onTabChange,
	...hookProps
}: RecommendationsPageProps) => {
	const {
		activeTab,
		setActiveTab,
		filteredCorrection,
		filteredMaintenance,
		filteredFoliar,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
		isChartsLoading,
		isChartsFetching,
		isChartsError,
		permissions,
		recommendationsPieData,
		recommendationsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		recommendationsFooterText,
	} = useRecommendationsPage(hookProps);
	const [hasRenderedContent, setHasRenderedContent] = useState(false);
	const isInitialLoading = !hasRenderedContent && isTableLoading && isChartsLoading;

	useEffect(() => {
		if (!isInitialLoading) setHasRenderedContent(true);
	}, [isInitialLoading]);

	const handleTabChange = useCallback(
		(tab: RecommendationsTab) => {
			setActiveTab(tab);
			onTabChange?.(tab);
		},
		[onTabChange, setActiveTab]
	);

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	if (isInitialLoading) return <RecommendationsPageSkeleton />;

	const tableError = isTableError ? (
		<div role="alert">
			<EmptyState
				title="Não foi possível carregar as recomendações"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
		</div>
	) : null;

	const tabs: SectionTab<RecommendationsTab>[] = [
		{
			key: 'correction',
			label: 'Correção',
			content: (
				<RecommendationsTable
					rows={filteredCorrection}
					onRowClick={onViewItem ? (id) => onViewItem('correction', id) : undefined}
				/>
			),
		},
		{
			key: 'maintenance',
			label: 'Manutenção',
			content: (
				<RecommendationsTable
					rows={filteredMaintenance}
					onRowClick={onViewItem ? (id) => onViewItem('maintenance', id) : undefined}
				/>
			),
		},
		{
			key: 'foliar',
			label: 'Foliares',
			content: (
				<RecommendationsTable
					rows={filteredFoliar}
					onRowClick={onViewItem ? (id) => onViewItem('foliar', id) : undefined}
				/>
			),
		},
	];

	const handleReport = () => onReportClick?.();
	const chartEmptyMessage = isChartsError ? 'Não foi possível carregar as estatísticas' : undefined;

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts>
					<ListPageChartCard isFetching={isChartsFetching && !isChartsLoading && !isChartsError}>
						<PiePercentageChart
							title="Distribuição de Recomendações"
							subtitle="Distribuição das recomendações de seus clientes"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={isChartsError ? [] : recommendationsPieData}
							isLoading={isChartsLoading}
							emptyMessage={chartEmptyMessage}
						/>
					</ListPageChartCard>
					<ListPageChartCard isFetching={isChartsFetching && !isChartsLoading && !isChartsError}>
						<ComparisonLineChart
							title="Execuções Nessa Semana"
							mainValue={chartMainValue}
							trendValue={chartTrendValue}
							trendLabel="vs semana anterior"
							trendDirection={chartTrendDirection}
							footerText={recommendationsFooterText}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={isChartsError ? [] : recommendationsLineData}
							isLoading={isChartsLoading}
							emptyMessage={chartEmptyMessage}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Execuções"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title="Gestão de Recomendações"
				subtitle="Controle os dados das recomendações de seus clientes"
				tabs={tabs.map((tab) => ({ ...tab, content: tableError ?? tab.content }))}
				isLoading={isTableLoading && !isTableError}
				isFetching={isTableFetching && !isTableError}
				loadingLabel="Carregando recomendações"
				activeTab={activeTab}
				onTabChange={handleTabChange}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
						customPanels={{ 'periodo:custom': renderPeriodPanel }}
					/>
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{permissions.canCreate && (
							<ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />
						)}
						<ActionButton text="Ver Relatório" onClick={handleReport} />
					</>
				}
			/>
		</ListPageLayout>
	);
};
